/**
 * Command for transforming BlockReference entities with non-uniform scaling.
 *
 * This command handles undo/redo for BlockReference resize operations
 * using setScaleFactors() and setPosition() instead of transformBy().
 */

import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModuleType } from "../DrawingSingleton";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";
import { findEntityByHandle } from "../core/oda";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";

/**
 * A BlockReference's absolute placement.
 *
 * This is a snapshot, not a transform: ODA's transformBy() does not scale a
 * BlockReference correctly, so these entities are moved and scaled by writing
 * setScaleFactors()/setPosition() outright. Undo restores the `before` values
 * rather than applying an inverse.
 */
export interface BlockRefState {
  scaleFactors: [number, number, number];
  position: [number, number, number];
}

/** Params for BlockReferenceTransformCommand: one entity, before and after. */
export interface BlockRefStateParams {
  handle: string;
  before: BlockRefState;
  after: BlockRefState;
}

/** Whether any entity's placement actually changed — worth an undo entry. */
export function blockRefStateChanged(params: BlockRefStateParams[]): boolean {
  return params.some(
    ({ before, after }) =>
      before.scaleFactors.some((v, i) => v !== after.scaleFactors[i]) ||
      before.position.some((v, i) => v !== after.position[i]),
  );
}

/**
 * Command to transform BlockReference entities with undo/redo support.
 */
export class BlockReferenceTransformCommand implements ICommand {
  private viewer: DRDrawingViewer;
  private module: DrawingModuleType;
  private params: BlockRefStateParams[];
  private executed = false;
  private oplogEvents: OplogEmit[] = [];

  constructor(viewer: DRDrawingViewer, params: BlockRefStateParams[]) {
    this.viewer = viewer;
    this.module = viewer.module;
    this.params = params;
  }

  /**
   * Execute the transform (transforms are already applied by handle).
   */
  execute(): void {
    this.oplogEvents = oplogEmitter.emitModified(
      this.viewer,
      this.params.map((p) => ({ handle: p.handle, changeKind: "moved" })),
    );

    if (this.executed) return;

    // Transforms are already applied by the handle during interaction
    // This command just tracks state for undo/redo
    this.executed = true;
    this.viewer.update();
  }

  /**
   * Undo the transform - restore original state.
   */
  undo(): void {
    if (!this.executed) return;
    this.applyState(true);
    this.executed = false;
    this.viewer.update();
    oplogEmitter.revert(this.oplogEvents);
  }

  /**
   * Redo the transform - apply new state.
   */
  redo(): void {
    if (this.executed) return;
    this.applyState(false);
    this.executed = true;
    this.viewer.update();
    oplogEmitter.reemit(this.oplogEvents);
  }

  /**
   * Apply state to BlockReference entities.
   * @param useOriginal - If true, apply original state (undo), else apply new state (redo)
   */
  private applyState(useOriginal: boolean): void {
    const db = this.viewer.database;
    if (!db) return;

    for (const param of this.params) {
      const entityId = findEntityByHandle(this.module, db, param.handle);
      if (!entityId) continue;

      const pObj = entityId.safeOpenObject(OpenMode.kForWrite, false); // kForWrite = 1
      const entity = this.module.OdDbEntity.cast(pObj);
      if (!entity) {
        deleteObj(pObj);
        continue;
      }

      const blockRef = this.module.OdDbBlockReference.cast(entity);
      if (!blockRef) {
        deleteObj(pObj);
        continue;
      }

      const target = useOriginal ? param.before : param.after;

      const scale = new this.module.OdGeScale3d(
        target.scaleFactors[0],
        target.scaleFactors[1],
        target.scaleFactors[2],
      );
      blockRef.setScaleFactors(scale);
      deleteObj(scale);

      const pos = new this.module.OdGePoint3d(
        target.position[0],
        target.position[1],
        target.position[2],
      );
      blockRef.setPosition(pos);
      deleteObj(pos);

      entity.recordGraphicsModified(true);
      deleteObj(pObj);
    }
  }

  /**
   * Clean up resources.
   */
  dispose(): void {
    // No resources to clean up
  }
}
